// INVLOC1 Slice 5 (docs/build-briefs/INVLOC1-S5-preferred-vendor-and-account-per-bar.md section 3.8; DEC-326). The two
// per-location tables, in the Slice 2 par table's pattern (one row per location, this tablet's location first, one Save): the item editor's
// "Preferred vendor by location" and the vendor card's "Account number by location". Both are admin surfaces (a laptop or PC, so a
// keyboard may be assumed), but the vendor is still a picker, never typed. Pure model: vendorLocationChoicesModel.ts.

import type {
  PreferredOfferLocationDto,
  VendorLocationAccountDto,
  VendorItemOfferDto,
} from "./api.ts";
import { sortByName } from "./nameSort.ts";
import {
  ACCOUNTS_BY_LOCATION_COPY,
  DEFAULT_CHOICE_VALUE,
  PREFERRED_BY_LOCATION_COPY,
  buildAccountEntries,
  buildPreferredEntries,
  defaultOptionLabel,
} from "./vendorLocationChoicesModel.ts";

export interface SaveResult {
  ok: boolean;
  message?: string;
}

function nameCell(locationName: string, isThisTablet: boolean, tag: string): HTMLElement {
  const cell = document.createElement("div");
  cell.setAttribute("role", "rowheader");
  const name = document.createElement("span");
  name.className = "inv-locations-location-name";
  name.textContent = locationName;
  cell.append(name);
  if (isThisTablet) {
    const tagEl = document.createElement("span");
    tagEl.className = "inv-locations-tag";
    tagEl.textContent = tag;
    cell.append(tagEl);
  }
  return cell;
}

function headRow(left: string, right: string): HTMLElement {
  const head = document.createElement("div");
  head.className = "inv-locations-head";
  head.setAttribute("role", "row");
  for (const text of [left, right]) {
    const cell = document.createElement("span");
    cell.setAttribute("role", "columnheader");
    cell.textContent = text;
    head.append(cell);
  }
  return head;
}

function heading(text: string, intro: string): HTMLElement[] {
  const h = document.createElement("p");
  h.className = "inv-locations-section-label";
  h.textContent = text;
  const p = document.createElement("p");
  p.className = "admin-field-help";
  p.style.marginTop = "0";
  p.textContent = intro;
  return [h, p];
}

const LOCK_SVG =
  '<svg class="inv-locations-lock" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">' +
  '<path fill="currentColor" d="M5 7V5a3 3 0 0 1 6 0v2h.5A1.5 1.5 0 0 1 13 8.5v5a1.5 1.5 0 0 1-1.5 1.5h-7A1.5 1.5 0 0 1 3 13.5v-5A1.5 1.5 0 0 1 4.5 7H5Zm1.5 0h3V5a1.5 1.5 0 0 0-3 0v2Z"/></svg>';

/** The quiet notes under a per-location table: what a Save does for a manager, or that the table is read-only for a viewer. */
function tableNote(
  canManage: boolean,
  savesRightAway: string,
  viewOnly: string,
  testId: string,
): HTMLElement {
  const note = document.createElement("p");
  note.className = canManage
    ? "admin-field-help inv-locations-note-line"
    : "admin-field-help inv-locations-note-line inv-locations-readonly-note";
  note.dataset["testid"] = testId;
  if (canManage) {
    note.textContent = savesRightAway;
  } else {
    note.innerHTML = LOCK_SVG;
    const text = document.createElement("span");
    text.textContent = viewOnly;
    note.append(text);
  }
  return note;
}

/** An offer as the picker names it: the vendor, its own code, the order unit and the listed cost. */
export function offerPickerLabel(
  offer: VendorItemOfferDto,
  vendorName: string,
  formatCents: (cents: number) => string,
): string {
  const sku = offer.vendor_sku === null ? "" : ` · SKU ${offer.vendor_sku}`;
  const unit = offer.order_unit === null ? "" : ` · ${offer.order_unit}`;
  const cost = offer.cost_cents === null ? "" : ` · ${formatCents(offer.cost_cents)}`;
  return `${vendorName}${sku}${unit}${cost}`;
}

/**
 * Builds the item editor's "Preferred vendor by location" table into `host`. One row per location the server allows; each picker
 * offers "Franchisee default (<vendor>)" and the item's ACTIVE offers. Save sends only the locations that changed.
 */
export function renderPreferredByLocation(
  host: HTMLElement,
  params: {
    locations: readonly PreferredOfferLocationDto[];
    defaultOfferId: string | null;
    offers: readonly VendorItemOfferDto[];
    vendorName: (vendorId: string) => string;
    formatCents: (cents: number) => string;
    canManage: boolean;
    save: (
      entries: Array<{ locationId: string; vendorItemOfferId: string | null }>,
    ) => Promise<SaveResult>;
  },
): void {
  host.innerHTML = "";
  const active = params.offers.filter((offer) => offer.active);
  const defaultOffer = active.find((offer) => offer.id === params.defaultOfferId);
  const defaultLabel =
    defaultOffer === undefined ? null : params.vendorName(defaultOffer.vendor_id);
  host.append(...heading(PREFERRED_BY_LOCATION_COPY.heading, PREFERRED_BY_LOCATION_COPY.intro));

  const table = document.createElement("div");
  table.className = params.canManage
    ? "inv-locations inv-locations--two"
    : "inv-locations inv-locations--two inv-locations--readonly";
  table.setAttribute("role", "table");
  table.setAttribute("aria-label", PREFERRED_BY_LOCATION_COPY.heading);
  table.dataset["testid"] = "admin-preferred-by-location-table";
  table.append(
    headRow(PREFERRED_BY_LOCATION_COPY.locationHeader, PREFERRED_BY_LOCATION_COPY.vendorHeader),
  );
  const pickers = new Map<string, HTMLSelectElement>();
  // UX88 (DEC-339): this tablet's location stays first, the rest read A to Z.
  for (const siteLocation of sortByName(params.locations, (listed) => listed.locationName, {
    pinned: (listed) => listed.isThisTablet,
  })) {
    const row = document.createElement("div");
    row.className = siteLocation.isThisTablet
      ? "inv-locations-row inv-locations-row--here"
      : "inv-locations-row";
    row.setAttribute("role", "row");
    row.dataset["testid"] = `admin-preferred-by-location-row-${siteLocation.locationId}`;
    const select = document.createElement("select");
    select.className = "field-input";
    select.setAttribute("aria-label", `Preferred vendor at ${siteLocation.locationName}`);
    select.dataset["testid"] = `admin-preferred-by-location-select-${siteLocation.locationId}`;
    const defaultOption = document.createElement("option");
    defaultOption.value = DEFAULT_CHOICE_VALUE;
    defaultOption.textContent = defaultOptionLabel(defaultLabel);
    select.append(defaultOption);
    // UX88 (DEC-339): the vendor choices read A to Z by the shown label.
    const labelled = sortByName(
      active.map((offer) => ({
        offer,
        label: offerPickerLabel(offer, params.vendorName(offer.vendor_id), params.formatCents),
      })),
      (entry) => entry.label,
    );
    for (const { offer, label } of labelled) {
      const option = document.createElement("option");
      option.value = offer.id;
      option.textContent = label;
      select.append(option);
    }
    select.value = siteLocation.chosenOfferId ?? DEFAULT_CHOICE_VALUE;
    select.disabled = !params.canManage;
    pickers.set(siteLocation.locationId, select);
    const cell = document.createElement("div");
    cell.setAttribute("role", "cell");
    cell.append(select);
    row.append(
      nameCell(
        siteLocation.locationName,
        siteLocation.isThisTablet,
        PREFERRED_BY_LOCATION_COPY.thisTabletTag,
      ),
      cell,
    );
    table.append(row);
  }
  host.append(table);

  const status = document.createElement("p");
  status.className = "admin-field-help";
  status.setAttribute("role", "status");
  status.dataset["testid"] = "admin-preferred-by-location-status";
  const saveButton = document.createElement("button");
  saveButton.type = "button";
  saveButton.className = "btn-secondary";
  saveButton.dataset["testid"] = "admin-preferred-by-location-save";
  saveButton.textContent = PREFERRED_BY_LOCATION_COPY.save;
  saveButton.disabled = !params.canManage;
  saveButton.addEventListener("click", () => {
    const picked = new Map([...pickers].map(([locationId, select]) => [locationId, select.value]));
    const entries = buildPreferredEntries(params.locations, picked);
    if (entries.length === 0) {
      status.textContent = PREFERRED_BY_LOCATION_COPY.nothingToSave;
      return;
    }
    saveButton.disabled = true;
    status.textContent = "";
    void params.save(entries).then((result) => {
      saveButton.disabled = !params.canManage;
      if (!result.ok) {
        status.textContent = `Could not save. ${result.message ?? "Check your connection."}`;
      } else {
        status.textContent = PREFERRED_BY_LOCATION_COPY.saved;
      }
    });
  });
  host.append(
    saveButton,
    tableNote(
      params.canManage,
      PREFERRED_BY_LOCATION_COPY.savesRightAway,
      PREFERRED_BY_LOCATION_COPY.viewOnly,
      "admin-preferred-by-location-note",
    ),
    status,
  );
}

/**
 * Builds a vendor card's "Account number by location" block: a details element that loads the locations only when opened (one
 * request per opened vendor, not one per vendor on every list render). Save sends only the locations that changed.
 */
export function buildVendorLocationAccounts(params: {
  vendorId: string;
  vendorName: string;
  canManage: boolean;
  load: () => Promise<
    { ok: true; accounts: VendorLocationAccountDto[] } | { ok: false; message: string }
  >;
  save: (
    entries: Array<{ locationId: string; accountReference: string | null }>,
  ) => Promise<SaveResult & { accounts?: VendorLocationAccountDto[] }>;
}): HTMLElement {
  const details = document.createElement("details");
  details.className = "admin-vendor-location-accounts";
  details.dataset["testid"] = "admin-vendor-location-accounts";
  const summary = document.createElement("summary");
  summary.textContent = ACCOUNTS_BY_LOCATION_COPY.heading;
  details.append(summary);
  const body = document.createElement("div");
  details.append(body);
  let loadedOnce = false;

  const paint = (accounts: VendorLocationAccountDto[]): void => {
    body.innerHTML = "";
    const intro = document.createElement("p");
    intro.className = "admin-field-help";
    intro.style.marginTop = "0";
    intro.textContent = ACCOUNTS_BY_LOCATION_COPY.intro;
    body.append(intro);
    const table = document.createElement("div");
    table.className = params.canManage
      ? "inv-locations inv-locations--two"
      : "inv-locations inv-locations--two inv-locations--readonly";
    table.setAttribute("role", "table");
    table.setAttribute(
      "aria-label",
      `${ACCOUNTS_BY_LOCATION_COPY.heading} for ${params.vendorName}`,
    );
    table.append(
      headRow(ACCOUNTS_BY_LOCATION_COPY.locationHeader, ACCOUNTS_BY_LOCATION_COPY.accountHeader),
    );
    const inputs = new Map<string, HTMLInputElement>();
    for (const siteLocation of sortByName(accounts, (listed) => listed.locationName, {
      pinned: (listed) => listed.isThisTablet,
    })) {
      const row = document.createElement("div");
      row.className = siteLocation.isThisTablet
        ? "inv-locations-row inv-locations-row--here"
        : "inv-locations-row";
      row.setAttribute("role", "row");
      row.dataset["testid"] = `admin-vendor-location-account-row-${siteLocation.locationId}`;
      const input = document.createElement("input");
      input.type = "text";
      // An account number is a code, not a word: no capital letters forced on, nothing remembered by the browser.
      input.inputMode = "text";
      input.setAttribute("autocapitalize", "off");
      input.autocomplete = "off";
      input.className = "field-input";
      // A view-only person is told a number is on file, never what it is (lead ruling 2026-10-09).
      input.placeholder =
        siteLocation.accountReference === null && siteLocation.hasNumber
          ? ACCOUNTS_BY_LOCATION_COPY.onFile
          : ACCOUNTS_BY_LOCATION_COPY.placeholder;
      input.value = siteLocation.accountReference ?? "";
      input.maxLength = 120;
      input.disabled = !params.canManage;
      input.setAttribute(
        "aria-label",
        `Account number at ${siteLocation.locationName} for ${params.vendorName}`,
      );
      input.dataset["testid"] = `admin-vendor-location-account-input-${siteLocation.locationId}`;
      inputs.set(siteLocation.locationId, input);
      const cell = document.createElement("div");
      cell.setAttribute("role", "cell");
      cell.append(input);
      row.append(
        nameCell(
          siteLocation.locationName,
          siteLocation.isThisTablet,
          ACCOUNTS_BY_LOCATION_COPY.thisTabletTag,
        ),
        cell,
      );
      table.append(row);
    }
    body.append(table);
    const status = document.createElement("p");
    status.className = "admin-field-help";
    status.setAttribute("role", "status");
    status.dataset["testid"] = "admin-vendor-location-accounts-status";
    const saveButton = document.createElement("button");
    saveButton.type = "button";
    saveButton.className = "btn-secondary";
    saveButton.dataset["testid"] = "admin-vendor-location-accounts-save";
    saveButton.textContent = ACCOUNTS_BY_LOCATION_COPY.save;
    saveButton.disabled = !params.canManage;
    saveButton.addEventListener("click", () => {
      const typed = new Map([...inputs].map(([locationId, input]) => [locationId, input.value]));
      const entries = buildAccountEntries(accounts, typed);
      if (entries.length === 0) {
        status.textContent = ACCOUNTS_BY_LOCATION_COPY.nothingToSave;
        return;
      }
      saveButton.disabled = true;
      status.textContent = "";
      void params.save(entries).then((result) => {
        saveButton.disabled = !params.canManage;
        if (!result.ok) {
          status.textContent = `Could not save. ${result.message ?? "Check your connection."}`;
          return;
        }
        if (result.accounts !== undefined) {
          paint(result.accounts);
          const fresh = body.querySelector<HTMLElement>(
            '[data-testid="admin-vendor-location-accounts-status"]',
          );
          if (fresh !== null) fresh.textContent = ACCOUNTS_BY_LOCATION_COPY.saved;
        }
      });
    });
    body.append(
      saveButton,
      tableNote(
        params.canManage,
        ACCOUNTS_BY_LOCATION_COPY.savesRightAway,
        ACCOUNTS_BY_LOCATION_COPY.viewOnly,
        "admin-vendor-location-accounts-note",
      ),
      status,
    );
  };

  details.addEventListener("toggle", () => {
    if (!details.open || loadedOnce) return;
    loadedOnce = true;
    body.textContent = "Loading your locations…";
    void params.load().then((result) => {
      if (!result.ok) {
        loadedOnce = false;
        body.textContent = `Could not load account numbers. ${result.message}`;
        return;
      }
      paint(result.accounts);
    });
  });
  return details;
}
